import { useState } from "react";
import type { FileResponse } from "@/lib/api";
import { saveFile } from "@/lib/downloadFile";
import { messageFor } from "@/lib/errors";
import { buttonClass } from "@/components/buttonStyles";

// A list row's Download (BRK-S406, shared since BRK-S410): the bytes come
// through the authed blob read the caller hands in, and the SERVER names the
// file (`saveFile`). Lifted above a row's stretched link so the button keeps
// its own click, and named by its row, because a column of identical
// "Download" buttons reads as one control to a screen reader. A failure says
// so in place, under the button, and the row stays — never a false success.

export function DocumentDownload({
  label,
  fallbackName,
  download,
}: {
  /** The button's accessible name, naming its row. */
  label: string;
  /** For a response that carries no `Content-Disposition`: a readable name,
   *  never an id. */
  fallbackName: string;
  download: () => Promise<FileResponse>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onClick() {
    setBusy(true);
    setError(null);
    try {
      saveFile(await download(), fallbackName);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="relative z-[1] mt-1 inline-flex max-w-full flex-col gap-1">
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        aria-label={label}
        className={buttonClass({ variant: "secondary", size: "sm" })}
      >
        {busy ? "Preparing…" : "Download"}
      </button>
      {error && (
        <span role="alert" className="text-xs text-brk-danger">
          {error}
        </span>
      )}
    </span>
  );
}
